import React, { useMemo, useState } from 'react';
import {
	Meta,
	useWorkplaceBasicDataQuery,
	useWorkplaceInsuranceCompaniesQuery,
} from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { useManagerContext } from '../../../../Services/ManagerContext';
import { updateWorkplaceData } from '../../../../Services/workplaceUpdate.service';
import { MetaKey } from '../../../../../Meta/Enums/MetaKey.enum';
import { TermType } from '../../../../../Term/Enums/TermType.enum';
import { getFormattedPriceAsFloat } from '../../../../../Base/Services/priceFormat.service';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import {
	getWorkplaceInsuranceCompaniesDetails,
	WorkplaceInsuranceCompany,
} from '../../../../Helpers/InsuranceCompanies/getWorkplaceInsuranceCompaniesIds';

const INVOICE_TERM_TYPES = [
	TermType.VZP_DPECE,
	TermType.VZP_INVOICE_TIME_PERIOD,
];

const POINTS_MULTIPLIER_KEY_PREFIX = 'pointsMultiplier_';

interface FormValues {
	careType: string | SelectValue;
	timePeriod: string | SelectValue;
	advanceCostTotal: string | number;
	dueDays: string | number;
	[key: string]: string | number | SelectValue;
}

interface Props {
	onSubmit: () => void;
}

const getSelectId = (value?: string | SelectValue) => {
	if (value == null) {
		return undefined;
	}

	if (typeof value === 'object') {
		return value.value != null ? String(value.value) : undefined;
	}

	return String(value);
};

const getPointsMultiplierFieldKey = (numberCode: string) =>
	`${POINTS_MULTIPLIER_KEY_PREFIX}${numberCode}`;

const formatInsuranceCompanyLabel = (company: WorkplaceInsuranceCompany) =>
	`${company.numberCode} - ${company.code}${
		company.name ? ` (${company.name})` : ''
	}`;

const ManagerHookUpInsuranceBatchInvoice: React.FC<Props> = ({ onSubmit }) => {
	const { workplaceId } = useManagerContext();
	const { displayError } = useApiClient();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [isSaving, setIsSaving] = useState(false);

	const { data, isLoading, refetch } = useWorkplaceBasicDataQuery({
		id: workplaceId,
	});

	const {
		data: workplaceInsuranceCompaniesData,
		isLoading: isLoadingInsuranceCompanies,
	} = useWorkplaceInsuranceCompaniesQuery({ workplaceId });

	const contractedInsuranceCompanies = useMemo(
		() =>
			getWorkplaceInsuranceCompaniesDetails(workplaceInsuranceCompaniesData),
		[workplaceInsuranceCompaniesData],
	);

	const formScheme: ItemScheme[] = useMemo(() => {
		const pointsMultiplierFields: ItemScheme[] =
			contractedInsuranceCompanies.length > 0
				? [
						{
							type: MagicField.TEXT,
							title: 'component.hookUp.insuranceBatch.pointsMultiplier',
						},
						...contractedInsuranceCompanies.map(company => ({
							type: MagicField.NUMBER,
							key: getPointsMultiplierFieldKey(company.numberCode),
							title: formatInsuranceCompanyLabel(company),
							settings: { required: true },
						})),
				  ]
				: [
						{
							type: MagicField.TEXT,
							title: 'component.errorMessages.noWorkplaceInsuranceCompanies',
						},
				  ];

		return [
			...pointsMultiplierFields,
			{
				type: MagicField.TERM_ITEM_SELECT,
				key: 'careType',
				title: 'component.hookUp.insuranceBatchInvoice.careType',
				settings: {
					required: true,
					portal: true,
					termType: TermType.VZP_DPECE,
					labelFormat: '{value} - {name}',
				},
			},
			{
				type: MagicField.TERM_ITEM_SELECT,
				key: 'timePeriod',
				title: 'component.hookUp.insuranceBatchInvoice.timePeriod',
				settings: {
					required: true,
					portal: true,
					termType: TermType.VZP_INVOICE_TIME_PERIOD,
					labelFormat: '{value} - {name}',
				},
			},
			{
				type: MagicField.PRICE_WITH_CURRENCY,
				key: 'advanceCostTotal',
				title: 'component.hookUp.insuranceBatchInvoice.advanceCostTotal',
				settings: { required: true },
			},
			{
				type: MagicField.NUMBER,
				key: 'dueDays',
				title: 'component.hookUp.insuranceBatchInvoice.dueDays',
				settings: { required: true },
			},
		];
	}, [contractedInsuranceCompanies]);

	const defaultValues = useMemo(() => {
		const workplace = data?.workplace;
		if (!workplace) return undefined;

		const meta = (workplace.meta as Meta[]) ?? [];
		const getMeta = (key: string) =>
			meta.find(item => item?.key === key)?.value;

		const pointsMultiplierMeta = getMeta(
			MetaKey.INSURANCE_BATCH_POINTS_MULTIPLIER,
		);
		const pointsMultiplierMap =
			pointsMultiplierMeta &&
			typeof pointsMultiplierMeta === 'object' &&
			!Array.isArray(pointsMultiplierMeta)
				? pointsMultiplierMeta
				: {};

		const pointsMultiplierDefaults = contractedInsuranceCompanies.reduce(
			(acc, company) => {
				acc[getPointsMultiplierFieldKey(company.numberCode)] =
					pointsMultiplierMap[company.numberCode] ?? '1';
				return acc;
			},
			{} as Record<string, string | number>,
		);

		return {
			careType: workplace.termItems?.find(
				item => item?.term?.type === TermType.VZP_DPECE,
			)?.id,
			timePeriod: workplace.termItems?.find(
				item => item?.term?.type === TermType.VZP_INVOICE_TIME_PERIOD,
			)?.id,
			advanceCostTotal: getMeta(
				MetaKey.INSURANCE_BATCH_INVOICE_ADVANCE_COST_TOTAL,
			),
			dueDays: getMeta(MetaKey.INSURANCE_BATCH_INVOICE_DUE_DAYS),
			...pointsMultiplierDefaults,
		};
	}, [data, contractedInsuranceCompanies]);

	const handleSubmit = async (values: FormValues) => {
		const careTypeId = getSelectId(values.careType);
		const timePeriodId = getSelectId(values.timePeriod);

		if (!careTypeId || !timePeriodId) {
			return;
		}

		setIsSaving(true);

		const existingTermItems =
			data?.workplace?.termItems
				?.filter(
					item =>
						item?.id &&
						item.term?.type &&
						!INVOICE_TERM_TYPES.includes(item.term.type as TermType),
				)
				.map(item => ({ id: item!.id })) ?? [];

		const pointsMultiplier = contractedInsuranceCompanies.reduce(
			(acc, company) => {
				const fieldKey = getPointsMultiplierFieldKey(company.numberCode);
				acc[company.numberCode] = Number(values[fieldKey]) || 1;
				return acc;
			},
			{} as Record<string, number>,
		);

		try {
			const response = await updateWorkplaceData(workplaceId, {
				termItems: [
					...existingTermItems,
					{ id: careTypeId },
					{ id: timePeriodId },
				],
				meta: [
					{
						key: MetaKey.INSURANCE_BATCH_INVOICE_ADVANCE_COST_TOTAL,
						value: String(getFormattedPriceAsFloat(values.advanceCostTotal)),
					},
					{
						key: MetaKey.INSURANCE_BATCH_INVOICE_DUE_DAYS,
						value: String(values.dueDays),
					},
					{
						key: MetaKey.INSURANCE_BATCH_POINTS_MULTIPLIER,
						value: pointsMultiplier,
					},
				],
			});

			if (response.status === 200) {
				await refetch();
				showNotificationSuccess({ content: t('base.saved') });
				onSubmit();
			} else {
				showNotificationAlert({ content: t('base.shared.warningBug') });
			}
		} catch (e) {
			displayError(e);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading || isLoadingInsuranceCompanies || isSaving) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		/>
	);
};

export default ManagerHookUpInsuranceBatchInvoice;
